import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Plus, Trash2, Image, ExternalLink, ChevronUp, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhotoSettingsPanel = () => {
  const [activeSubTab, setActiveSubTab] = useState('gallery');
  const [settings, setSettings] = useState({
    frame_enabled: true,
    frame_color: '#000000',
    frame_stroke_width: 2,
    spotlight_enabled: true,
    shadow_enabled: true,
    shadow_strength: 50,
    shadow_blur: 40
  });
  const [galleryItems, setGalleryItems] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, galleryRes, collectionsRes] = await Promise.all([
        fetch(`${API_URL}/api/content/photo-settings`),
        fetch(`${API_URL}/api/content/photo-gallery`),
        fetch(`${API_URL}/api/content/collections`)
      ]);
      
      const settingsData = await settingsRes.json();
      const galleryData = await galleryRes.json();
      const collectionsData = await collectionsRes.json();
      
      setSettings(settingsData);
      setGalleryItems(galleryData.items || []);
      setCollections(collectionsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setMessage('');
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/content/photo-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        setMessage('Settings saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await res.json();
        setMessage(`Error: ${error.detail || 'Failed to save settings'}`);
      }
    } catch (error) {
      console.error('Error saving photo settings:', error);
      setMessage('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCollection = async (collectionId) => {
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/content/photo-gallery/item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ collection_id: collectionId, order: galleryItems.length })
      });
      
      if (res.ok) {
        const data = await res.json();
        setGalleryItems(data.gallery.items || []);
        setShowCollectionPicker(false);
        setMessage('Collection added to gallery!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error adding collection:', error);
      setMessage('Error adding collection');
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Remove this collection from the gallery?')) return;
    
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/content/photo-gallery/item/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setGalleryItems(data.gallery.items || []);
        setMessage('Collection removed from gallery');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const getCollectionById = (id) => {
    return collections.find(c => c.id === id);
  };

  // Move item up/down and save new order
  const handleMove = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= galleryItems.length) return;

    const reordered = [...galleryItems];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, moved);
    setGalleryItems(reordered);

    try {
      const token = api.getToken();
      const itemIds = reordered.map(item => item.id);
      const res = await fetch(`${API_URL}/api/content/photo-gallery/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(itemIds)
      });
      if (res.ok) {
        setMessage('Order updated!');
        setTimeout(() => setMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error saving order:', error);
      setMessage('Error saving order');
    }
  };

  // Get collections not yet in the gallery
  const availableCollections = collections.filter(
    c => !galleryItems.some(item => item.collection_id === c.id)
  );

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Photo Gallery Settings</h2>
          <p className="text-sm text-gray-500 mt-1">Configure the art gallery appearance and content</p>
        </div>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${
          message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
        }`}>
          {message}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-4 mb-6 border-b">
        {[
          { id: 'gallery', label: 'Gallery Content' },
          { id: 'appearance', label: 'Appearance Settings' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === tab.id
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Gallery Content Tab */}
      {activeSubTab === 'gallery' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              Select which collections appear in the Photo Gallery slider. Use arrows to reorder.
            </p>
            <button
              onClick={() => setShowCollectionPicker(true)}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800"
              disabled={availableCollections.length === 0}
            >
              <Plus size={16} /> Add Collection
            </button>
          </div>

          {/* Gallery Items List */}
          <div className="space-y-3">
            {galleryItems.length === 0 ? (
              <div className="border-2 border-dashed rounded-lg p-12 text-center">
                <Image size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-2">No collections in gallery yet</p>
                <p className="text-sm text-gray-400">Add collections from your library to display in the Photo Gallery slider</p>
              </div>
            ) : (
              galleryItems.map((item, index) => {
                const collection = getCollectionById(item.collection_id);
                return (
                  <div
                    key={item.id}
                    className="flex items-center gap-4 p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                    data-testid={`gallery-item-${index}`}
                  >
                    {/* Up/Down arrows */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleMove(index, -1)}
                        disabled={index === 0}
                        className={`p-1 rounded transition-colors ${index === 0 ? 'text-gray-200 cursor-not-allowed' : 'text-gray-400 hover:text-black hover:bg-gray-100'}`}
                        title="Move up"
                        data-testid={`move-up-${index}`}
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        onClick={() => handleMove(index, 1)}
                        disabled={index === galleryItems.length - 1}
                        className={`p-1 rounded transition-colors ${index === galleryItems.length - 1 ? 'text-gray-200 cursor-not-allowed' : 'text-gray-400 hover:text-black hover:bg-gray-100'}`}
                        title="Move down"
                        data-testid={`move-down-${index}`}
                      >
                        <ChevronDown size={16} />
                      </button>
                    </div>
                    <span className="text-sm font-medium text-gray-400 w-6">{index + 1}</span>
                    
                    {/* Thumbnail */}
                    <div className="w-20 h-14 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                      {collection?.cover_image_url ? (
                        <img 
                          src={collection.cover_image_url} 
                          alt={collection.couple} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Image size={20} />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1">
                      <h4 className="font-medium">
                        {collection?.couple || 'Unknown Collection'}
                      </h4>
                      <p className="text-sm text-gray-500">{collection?.location || ''}</p>
                    </div>
                    
                    {/* Actions */}
                    <a
                      href={`/collection/${item.collection_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-400 hover:text-blue-500"
                      title="View collection"
                    >
                      <ExternalLink size={16} />
                    </a>
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="p-2 text-gray-400 hover:text-red-500"
                      title="Remove from gallery"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {galleryItems.length > 0 && (
            <p className="text-xs text-gray-400 text-center">
              {galleryItems.length} collection{galleryItems.length !== 1 ? 's' : ''} in gallery
            </p>
          )}
        </div>
      )}

      {/* Appearance Settings Tab */}
      {activeSubTab === 'appearance' && (
        <div className="space-y-6">
          {/* Frame Settings */}
          <div className="border rounded-lg p-6">
            <h3 className="font-medium mb-4">Image Frame Settings</h3>
            
            <div className="space-y-4">
              {/* Frame Enable/Disable */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium">Frame Enabled</label>
                  <p className="text-sm text-gray-500">Show frames around gallery images</p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, frame_enabled: !prev.frame_enabled }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    settings.frame_enabled ? 'bg-black' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    settings.frame_enabled ? 'translate-x-6' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Frame Color */}
              <div>
                <label className="block font-medium mb-2">Frame Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings.frame_color || '#000000'}
                    onChange={(e) => setSettings(prev => ({ ...prev, frame_color: e.target.value }))}
                    className="w-12 h-10 rounded border cursor-pointer"
                    disabled={!settings.frame_enabled}
                  />
                  <input
                    type="text"
                    value={settings.frame_color || '#000000'}
                    onChange={(e) => setSettings(prev => ({ ...prev, frame_color: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded text-sm"
                    placeholder="#000000"
                    disabled={!settings.frame_enabled}
                  />
                </div>
              </div>

              {/* Frame Stroke Width */}
              <div>
                <label className="block font-medium mb-2">Frame Stroke Width: {settings.frame_stroke_width || 2}px</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={settings.frame_stroke_width || 2}
                  onChange={(e) => setSettings(prev => ({ ...prev, frame_stroke_width: parseInt(e.target.value) }))}
                  className="w-full"
                  disabled={!settings.frame_enabled}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Thin (1px)</span>
                  <span>Thick (10px)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Shadow Settings */}
          <div className="border rounded-lg p-6">
            <h3 className="font-medium mb-4">Shadow Effect</h3>
            
            <div className="space-y-4">
              {/* Shadow Enable/Disable */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium">Shadow Enabled</label>
                  <p className="text-sm text-gray-500">Add soft blurred shadows behind collections</p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, shadow_enabled: !prev.shadow_enabled }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    settings.shadow_enabled ? 'bg-black' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    settings.shadow_enabled ? 'translate-x-6' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Shadow Strength */}
              <div>
                <label className="block font-medium mb-2">Shadow Strength: {settings.shadow_strength || 50}%</label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={settings.shadow_strength || 50}
                  onChange={(e) => setSettings(prev => ({ ...prev, shadow_strength: parseInt(e.target.value) }))}
                  className="w-full"
                  disabled={!settings.shadow_enabled}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Subtle</span>
                  <span>Strong</span>
                </div>
              </div>

              {/* Shadow Blur */}
              <div>
                <label className="block font-medium mb-2">Shadow Blur: {settings.shadow_blur || 40}px</label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={settings.shadow_blur || 40}
                  onChange={(e) => setSettings(prev => ({ ...prev, shadow_blur: parseInt(e.target.value) }))}
                  className="w-full"
                  disabled={!settings.shadow_enabled}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Sharp</span>
                  <span>Soft</span>
                </div>
              </div>
            </div>
          </div>

          {/* Spotlight Settings */}
          <div className="border rounded-lg p-6">
            <h3 className="font-medium mb-4">Spotlight Effect</h3>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="font-medium">Spotlight Enabled</label>
                <p className="text-sm text-gray-500">Add ambient gallery lighting effect above images</p>
              </div>
              <button
                onClick={() => setSettings(prev => ({ ...prev, spotlight_enabled: !prev.spotlight_enabled }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  settings.spotlight_enabled ? 'bg-black' : 'bg-gray-300'
                }`}
              >
                <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  settings.spotlight_enabled ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="border rounded-lg p-6 bg-gray-50">
            <h3 className="font-medium mb-4">Preview</h3>
            <div className="flex justify-center py-8">
              <div 
                className={settings.spotlight_enabled ? 'relative' : ''}
                style={{
                  ...(settings.spotlight_enabled ? {
                    boxShadow: '0 -20px 60px -10px rgba(255,255,255,0.4)'
                  } : {}),
                  ...(settings.shadow_enabled ? {
                    filter: `drop-shadow(0 ${settings.shadow_blur * 0.5}px ${settings.shadow_blur}px rgba(0,0,0,${settings.shadow_strength / 100 * 0.4}))`
                  } : {})
                }}
              >
                {settings.spotlight_enabled && (
                  <div 
                    className="absolute -top-8 left-1/2 -translate-x-1/2 w-3/4 h-8"
                    style={{
                      background: 'radial-gradient(ellipse at center bottom, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 70%)'
                    }}
                  />
                )}
                <div 
                  style={settings.frame_enabled ? {
                    padding: (settings.frame_stroke_width || 2) * 4,
                    backgroundColor: settings.frame_color || '#000000'
                  } : {}}
                >
                  <div className="w-48 h-64 bg-gray-200 flex items-center justify-center text-gray-400 text-sm">
                    Sample Image
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {/* Collection Picker Modal */}
      {showCollectionPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Add Collection to Gallery</h3>
              <button 
                onClick={() => setShowCollectionPicker(false)}
                className="text-gray-400 hover:text-black text-2xl"
              >
                &times;
              </button>
            </div>
            
            {availableCollections.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                All collections are already in the gallery
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 overflow-y-auto">
                {availableCollections.map(collection => (
                  <button
                    key={collection.id}
                    onClick={() => handleAddCollection(collection.id)}
                    className="text-left border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-[4/3] bg-gray-100">
                      {collection.cover_image_url ? (
                        <img 
                          src={collection.cover_image_url} 
                          alt={collection.couple} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Image size={32} />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-sm">{collection.couple}</h4>
                      <p className="text-xs text-gray-500">{collection.location}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoSettingsPanel;
